/**
 * Google Maps Component
 * Initializes Google Maps instances found in the DOM.
 * Uses global settings passed from app.js for API key and defaults.
 * Handles dynamic loading of the Google Maps API script.
 */

import { Loader } from '@googlemaps/js-api-loader';
import { debounce } from '../core/debounce.js';
import { isTouch } from '../core/is-touch.js';
import { getLenisInstance } from '../core/scroll-manager.js';

// Export the main initialization function
export function googleMaps(pageMapConfigs = {}, globalSettings, log = false) {

	// Check if a valid API key was passed
	if (!globalSettings || !globalSettings.apiKey) {
		if (log) console.warn('Google Maps: API key not provided in globalSettings. Cannot initialize maps.');
		return;
	}

	// Find map elements in the DOM
	const maps = document.querySelectorAll('.google-map__canvas');
	if (!maps || maps.length === 0) {
		if (log) console.log('Google Maps: No map canvas elements found in the DOM.');
		return;
	}

	// Use default settings passed from app.js
	const defaultMapStyles = globalSettings.defaultStyles || [];
	const defaultMapMarker = globalSettings.defaultMarker || null; // Expects { url, width, height } or null

	// Configure the loader
	const mapsLoader = new Loader({
	  apiKey: globalSettings.apiKey,
	  version: "weekly",
	  libraries: ["places", "geometry"] // Added geometry for spherical calculations
	});

	// Load the Google Maps API
	mapsLoader.load().then((google) => {
		if (log) console.log('Google Maps: API loaded successfully.');

		// Initialize each map found in the DOM
		maps.forEach(function(mapCanvasElement) {
			const mapContainer = mapCanvasElement.closest('.google-map');
			if (!mapContainer || !mapContainer.id) {
				console.error('Google Maps: Found a canvas without a valid parent .google-map container or ID.');
				return;
			}

			const mapFullID = mapContainer.getAttribute("id"); // Use the full ID like 'google-map--v7sdgfso8'
			const mapShortID = mapFullID.replace('google-map--', ''); // e.g., 'v7sdgfso8'
			let mapSettings = pageMapConfigs[mapShortID] || {}; // Load specific settings from the passed config object.

			// --- Fallback/Default Settings if specific settings object not found ---
			if (Object.keys(mapSettings).length === 0) {
				if (log) console.log(`Google Maps: No specific settings object found for ${mapFullID}. Using data attributes.`);
				mapSettings = {
					centerMarker: {
						position: {
							lat: parseFloat(mapContainer.getAttribute('data-latitude')) || 0,
							lng: parseFloat(mapContainer.getAttribute('data-longitude')) || 0
						}
					},
					zoom: parseInt(mapContainer.getAttribute('data-zoom')) || 14,
					primaryMarker: { // Assume primary marker is same as center if not specified
						position: {
							lat: parseFloat(mapContainer.getAttribute('data-latitude')) || 0,
							lng: parseFloat(mapContainer.getAttribute('data-longitude')) || 0
						},
						icon: null // Will fallback to defaultMapMarker later
					},
					markers: [], // Assume no extra markers unless defined in specific settings
					directions: { enable_directions: false },
					radial: { enable: false },
					dialogType: 'dialog' // Default dialog type
				};
			}

			// Ensure required properties exist
			mapSettings.centerMarker = mapSettings.centerMarker || {};
			mapSettings.centerMarker.position = mapSettings.centerMarker.position || { lat: 0, lng: 0 };
			mapSettings.primaryMarker = mapSettings.primaryMarker || {};
			mapSettings.primaryMarker.position = mapSettings.primaryMarker.position || mapSettings.centerMarker.position;
			mapSettings.zoom = mapSettings.zoom || 14;
			mapSettings.markers = mapSettings.markers || [];
			mapSettings.directions = mapSettings.directions || { enable_directions: false };
			mapSettings.radial = mapSettings.radial || { enable: false };
			mapSettings.dialogType = mapSettings.dialogType || 'dialog'; // Default to 'dialog'

			const touch = isTouch();

			// --- Base Google Map Options ---
			let googleMapSettings = {
				zoom: mapSettings.zoom,
				center: { lat: mapSettings.centerMarker.position.lat, lng: mapSettings.centerMarker.position.lng },
				disableDefaultUI: false,
	            mapTypeControl: false,
	            disableDoubleClickZoom: false,
	            draggable: true,
	            keyboardShortcuts: false,
	            scrollwheel: false,
	            streetViewControl: false,
	            zoomControl: true,
	            zoomControlOptions: {
	                position: google.maps.ControlPosition.RIGHT_CENTER
	            },
	            mapTypeId: google.maps.MapTypeId.ROADMAP,
	            styles: mapSettings.styles || defaultMapStyles // Use specific styles or fallback to global default
			};

			// Adjust options for touch devices
			if (touch) {
				googleMapSettings.disableDefaultUI = true;
	            googleMapSettings.disableDoubleClickZoom = true;
	            googleMapSettings.draggable = false;
	            googleMapSettings.panControl = true; // Keep pan control maybe?
			}

			// --- Initialize Map ---
			let googleMap;
			try {
				googleMap = new google.maps.Map(mapCanvasElement, googleMapSettings);
				if (log) console.log(`Google Maps: Map initialized for ${mapFullID}`);
			} catch (error) {
				console.error(`Google Maps: Error initializing map for ${mapFullID}`, error);
				return; // Stop processing this map if initialization failed
			}

			let bounds = new google.maps.LatLngBounds();

			// --- Directions Setup ---
			let directionsService = null;
			let directionsRenderer = null;
			if (mapSettings.directions.enable_directions === true) {
				directionsService = new google.maps.DirectionsService();
				directionsRenderer = new google.maps.DirectionsRenderer({
					map: googleMap,
					suppressMarkers: true, // We handle markers manually
					polylineOptions: {
						strokeOpacity: 0, // Start invisible, animate in later
					}
				});
				if (log) console.log(`Google Maps: Directions service enabled for ${mapFullID}`);
			}

			// --- Primary Marker Setup ---
			let primaryMarkerIconOptions = null;
			// Use specific icon if provided, otherwise fallback to global default
			const specificPrimaryIcon = mapSettings.primaryMarker.icon;
			const iconToUse = specificPrimaryIcon || defaultMapMarker;

			if (iconToUse && iconToUse.url && iconToUse.width && iconToUse.height) {
				const width = parseInt(iconToUse.width);
				const height = parseInt(iconToUse.height);
				primaryMarkerIconOptions = {
					url: iconToUse.url,
					scaledSize: new google.maps.Size(width, height),
					origin: new google.maps.Point(0, 0),
					anchor: new google.maps.Point(width / 2, height), // Center bottom anchor
					// labelOrigin: new google.maps.Point(width / 4, height / 5) // Adjust label origin if needed
				};
			} else if (log) {
				console.log(`Google Maps: No valid primary marker icon found for ${mapFullID}, using default Google marker.`);
			}

			// Create the primary/parent marker
			let parentMarker = new google.maps.Marker({
				position: { lat: mapSettings.primaryMarker.position.lat, lng: mapSettings.primaryMarker.position.lng },
				map: googleMap,
				markerID: 'parent', // Custom attribute
				icon: primaryMarkerIconOptions, // Use prepared icon options or null for default
				zIndex: 100 // Ensure parent is usually above children
	        });

			// Store the original size on the marker object for responsive scaling.
			if (iconToUse && iconToUse.width && iconToUse.height) {
				parentMarker.originalSize = {
					width: parseInt(iconToUse.width),
					height: parseInt(iconToUse.height)
				};
			}
	        bounds.extend(parentMarker.getPosition()); // Include parent in bounds calculation

	        // Info window for parent marker (if dialog content exists)
	        let parentInfoWindow = null;
	        if (mapSettings.primaryMarker.dialog) {
		        parentInfoWindow = new google.maps.InfoWindow({
					content: `<div class="dialog-wrap">${htmlDecode(mapSettings.primaryMarker.dialog)}</div>`,
					disableAutoPan: false,
			    });

		        parentMarker.addListener('mouseover', () => {
		        	if (parentInfoWindow) parentInfoWindow.open(googleMap, parentMarker);
		        });
		        parentMarker.addListener('mouseout', () => {
		        	if (parentInfoWindow) parentInfoWindow.close();
		        });
		    }

			// --- Child Markers Setup ---
	        let childrenMarkers = []; // Array to hold the google.maps.Marker objects
	        let infoWindows = []; // Array for child info windows, stored as { markerID, window }
	        let polylines = []; // Array for direction polylines
	        let childIndex = 0;

	        if (mapSettings.markers.length > 0) {
			    mapSettings.markers.forEach((markerConfig) => {
			    	childIndex++;

			    	// --- Prepare Child Marker Icon ---
			    	let childMarkerIconOptions = null;
			    	const specificChildIcon = markerConfig.markerIcon; // Assuming ACF field name is markerIcon
			    	const childIconToUse = specificChildIcon || defaultMapMarker; // Fallback to global default

			    	if (childIconToUse && childIconToUse.url && childIconToUse.width && childIconToUse.height) {
			    		const width = parseInt(childIconToUse.width);
			    		const height = parseInt(childIconToUse.height);
			    		childMarkerIconOptions = {
							url: childIconToUse.url,
							scaledSize: new google.maps.Size(width, height),
							origin: new google.maps.Point(0, 0),
							anchor: new google.maps.Point(width / 2, height),
							labelOrigin: new google.maps.Point(width / 2, height / 2.5) // Adjust as needed
						};
			    	}

			    	// --- Prepare Child Marker Label ---
			    	let childMarkerLabel = null;
			    	if (mapSettings.markerLabels) { // Check if label style is defined
			    		childMarkerLabel = {
			    			...mapSettings.markerLabels, // Copy style properties (color, fontSize etc.)
			    			text: childIndex.toString(), // Set the text to the index
			    			className: "pin-label" // Add class for potential styling
			    		};
			    	}

					// --- Create Child Marker ---
					const childMarker = new google.maps.Marker({
						position: markerConfig.position, // Assuming {lat: ..., lng: ...}
						map: googleMap,
						icon: childMarkerIconOptions,
						label: childMarkerLabel,
						markerID: markerConfig.markerID ?? `child-${childIndex}`, // Use provided ID or generate one
						pathDisplay: markerConfig.pathDisplay || 'DRIVING', // Custom attribute, default to DRIVING
						groupID: markerConfig.groupID ?? 'group-default', // Custom attribute
						dialog: markerConfig.dialog || '' // Store dialog content
					});

					// Store the original size on the marker object for responsive scaling.
					if (childIconToUse && childIconToUse.url && childIconToUse.width && childIconToUse.height) {
						childMarker.originalSize = {
							width: parseInt(childIconToUse.width),
							height: parseInt(childIconToUse.height)
						};
					}

					// Store the original label font size for responsive scaling.
					if (childMarkerLabel && childMarkerLabel.fontSize) {
						childMarker.originalLabelFontSize = parseFloat(childMarkerLabel.fontSize);
					}

			    	bounds.extend(childMarker.getPosition());
			    	childrenMarkers.push(childMarker);

			    	// --- Child InfoWindow ---
	 				let infoWindow = null;
	 				// Create InfoWindow if dialog type allows, and content exists or directions are enabled
	 				if ((mapSettings.dialogType === 'dialog' || mapSettings.dialogType === 'both') && (childMarker.dialog || mapSettings.directions.enable_directions)) {
						
						// Start with the marker's own dialog content.
						let dialogHtml = childMarker.dialog ? htmlDecode(childMarker.dialog) : '';
						let directionsHtml = '';

						// If directions are enabled, prepare the HTML for it.
						if (mapSettings.directions.enable_directions) {
							// If there's no custom dialog content, provide a default title.
							if (!dialogHtml) {
								dialogHtml = 'Route Details';
							}
							// Create the directions part with placeholders. Add a line break if there's dialog content.
							directionsHtml = `${dialogHtml ? '<br>' : ''}Driving: [driving-distance]km ([driving-time])<br>Walking: [walking-distance]km ([walking-time])`;
						}

						// Only create the InfoWindow if there's some content to show.
						if (dialogHtml) {
							const finalContent = `<div class="dialog-wrap">${dialogHtml}${directionsHtml}</div>`;
							infoWindow = new google.maps.InfoWindow({ content: finalContent, disableAutoPan: false });
							infoWindows.push({ markerID: childMarker.markerID, window: infoWindow }); // Add to array for managing close events
						}
					}

					// --- Directions Logic for Child ---
					if (directionsService && directionsRenderer) {
						// Request Walking Directions
						directionsService.route({
							origin: parentMarker.getPosition(),
							destination: childMarker.getPosition(),
							travelMode: 'WALKING'
					    }).then(response => {
					    	// Store results or update UI immediately
					    	const route = response.routes[0];
					    	if (route && route.legs[0]) {
						        const leg = route.legs[0];
						        const walkingDistance = (leg.distance.value / 1000).toFixed(2);
						        const walkingTime = leg.duration.text;

						        // Update InfoWindow content if it exists
						        if (infoWindow) {
						        	let currentContent = infoWindow.getContent();
						        	currentContent = currentContent.replace("[walking-distance]", walkingDistance).replace("[walking-time]", walkingTime);
						        	infoWindow.setContent(currentContent);
						        }

						        // If this marker should display the walking path
								if (childMarker.pathDisplay === 'WALKING') {
									const path = google.maps.geometry.encoding.decodePath(route.overview_polyline);
									const polyline = new google.maps.Polyline({
										path: path,
										map: googleMap,
										geodesic: true,
										strokeColor: rgbaToHex(mapSettings.directions.direction_styles?.fill_colour_default || 'rgba(255,0,0,1)').hex, // Default red
										strokeWeight: Number(mapSettings.directions.direction_styles?.stroke_width || 4),
										strokeOpacity: 0, // Start hidden
									});
									polylines.push({ markerID: childMarker.markerID, polyline: polyline, type: 'WALKING' });
								}
							}
					    }).catch(e => console.error(`Directions request failed for WALKING to ${childMarker.markerID}: ${e.toString()}`));

						// Request Driving Directions
						directionsService.route({
							origin: parentMarker.getPosition(),
							destination: childMarker.getPosition(),
							travelMode: 'DRIVING'
						}).then(response => {
							const route = response.routes[0];
					    	if (route && route.legs[0]) {
						        const leg = route.legs[0];
						        const drivingDistance = (leg.distance.value / 1000).toFixed(2);
						        const drivingTime = leg.duration.text;

						        // Update InfoWindow content
						        if (infoWindow) {
						        	let currentContent = infoWindow.getContent();
						        	currentContent = currentContent.replace("[driving-distance]", drivingDistance).replace("[driving-time]", drivingTime);
						        	infoWindow.setContent(currentContent);
						        }

						        // If this marker should display the driving path
								if (childMarker.pathDisplay === 'DRIVING') {
									const path = google.maps.geometry.encoding.decodePath(route.overview_polyline);
									const polyline = new google.maps.Polyline({
										path: path,
										map: googleMap,
										strokeColor: rgbaToHex(mapSettings.directions.direction_styles?.fill_colour_default || 'rgba(0,0,255,1)').hex, // Default blue
										strokeWeight: Number(mapSettings.directions.direction_styles?.stroke_width || 4),
										strokeOpacity: 0, // Start hidden
									});
									polylines.push({ markerID: childMarker.markerID, polyline: polyline, type: 'DRIVING' });
								}
							}
						}).catch(e => console.error(`Directions request failed for DRIVING to ${childMarker.markerID}: ${e.toString()}`));
					} // End directions logic

			    }); // End forEach markerConfig
			} // End if mapSettings.markers.length > 0

			// --- Fit Bounds ---
		    if (childrenMarkers.length > 0) { // Only fit if there are child markers
				fitBoundsToVisibleMarkers(googleMap, childrenMarkers.concat(parentMarker), mapSettings.zoom); // Include parent marker in bounds
			} else {
				// If only parent marker, just center and zoom
			    googleMap.setCenter(parentMarker.getPosition());
			    googleMap.setZoom(mapSettings.zoom);
			}

			// --- Radial Circles Setup ---
			let circles = [];
			let labels = [];
			const transparentMarkerIcon = { // Define transparent icon for labels
				url: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=', // 1x1 transparent png
				size: new google.maps.Size(1, 1),
				origin: new google.maps.Point(0, 0),
				anchor: new google.maps.Point(0, 0)
			};

			if (mapSettings.radial.enable === true && mapSettings.radial.distances && mapSettings.radial.distances.length > 0) {
				if (log) console.log(`Google Maps: Setting up radial circles for ${mapFullID}`);
				const radialStyles = mapSettings.radial.radial_styles || {};
				const labelStyles = mapSettings.radial.label_styles || {};

				// Optional Background Circle (covers area beyond largest radius)
				const bgCircle = new google.maps.Circle({
					strokeOpacity: 0, // No border
					strokeWeight: 0,
					fillColor: rgbaToHex(radialStyles.background_colour_default || 'rgba(0,0,0,0.1)').hex,
					fillOpacity: rgbaToHex(radialStyles.background_colour_default || 'rgba(0,0,0,0.1)').opacity,
					map: googleMap,
					center: parentMarker.getPosition(),
					radius: (Number(mapSettings.radial.distances[mapSettings.radial.distances.length - 1]) || 5000) * 1.1, // Slightly larger than largest radius
					zIndex: 1,
					clickable: false
				});

				// Create circles for each distance
				mapSettings.radial.distances.forEach((distance) => {
					const radius = Number(distance);
					if (isNaN(radius) || radius <= 0) return; // Skip invalid distances

					circles.push(new google.maps.Circle({
						strokeColor: rgbaToHex(radialStyles.boundary_colour_default || 'rgba(255,255,0,0.8)').hex,
						strokeOpacity: rgbaToHex(radialStyles.boundary_colour_default || 'rgba(255,255,0,0.8)').opacity,
						strokeWeight: Number(radialStyles.boundary_stroke_width || 1),
						fillColor: rgbaToHex(radialStyles.background_colour_active || 'rgba(255,255,0,0.1)').hex, // Color when active
						fillOpacity: 0, // Start transparent
						map: googleMap,
						center: parentMarker.getPosition(),
						radius: radius,
						zIndex: 2, // Above background circle
						clickable: false
					}));

					// Add labels if enabled
					if (labelStyles.enable_labels === true) {
						let circleLabelText = radius + 'm';
						if (radius >= 1000) {
							circleLabelText = (radius / 1000) + 'km';
						}

						const destination = calculateLabelDestination(parentMarker.getPosition().lat(), parentMarker.getPosition().lng(), radius);

						labels.push(new google.maps.Marker({
							position: destination,
							map: googleMap,
							icon: transparentMarkerIcon, // Use the 1x1 transparent icon
							label: {
								text: circleLabelText,
								color: rgbaToHex(labelStyles.label_text_colour || 'rgba(255,255,0,1)').hex,
								// colorOpacity: rgbaToHex(labelStyles.label_text_colour || 'rgba(255,255,0,1)').opacity, // Opacity not directly supported on label color
								fontSize: labelStyles.label_font_size || '10px',
								className: "google-map__radial-label" // For CSS styling
							},
							zIndex: 3 // Above circles
						}));
					}
				});
			} // End radial setup

			// --- Map Click Listener (to close things) ---
			googleMap.addListener("click", function(event) {
				if (parentInfoWindow) parentInfoWindow.close();
			    infoWindows.forEach((iw) => iw.window.close());
				polylines.forEach((p) => animatePolylineOut(p.polyline));
				closeAllMarkerPanels(mapFullID);
				dimAllCircles(circles);
			});

			// --- Control Button Listeners ---
			const mapControlsContainer = document.querySelector(`#${mapFullID}`)?.closest('.layout__google_map'); // Find the parent layout container

			if (mapControlsContainer) {
				// Category Controls
				const categoryControls = mapControlsContainer.querySelectorAll('.google-map__controls [data-control-type="categories"] button');
				categoryControls.forEach(button => {
					button.addEventListener("click", () => {
						const groupID = button.dataset.groupId;
						if (log) console.log(`Google Maps (${mapFullID}): Category filter clicked - ${groupID}`);

						// Reset UI
						categoryControls.forEach(btn => btn.classList.remove('selected'));
						button.classList.add('selected');
						if (parentInfoWindow) parentInfoWindow.close();
						infoWindows.forEach(iw => iw.window.close());
						polylines.forEach(p => polylineOut(p.polyline)); // Use instant out for filters
						closeAllMarkerPanels(mapFullID);
						dimAllCircles(circles);

						// Filter markers
						let visibleMarkers = [];
						childrenMarkers.forEach(child => {
							const isVisible = (groupID === 'all' || child.groupID === groupID);
							child.setVisible(isVisible);
							if (isVisible) visibleMarkers.push(child);
						});

						// Refit bounds if necessary
						if (visibleMarkers.length > 0) {
							fitBoundsToVisibleMarkers(googleMap, visibleMarkers.concat(parentMarker), mapSettings.zoom);
						} else {
							// Optionally zoom to parent if no children match
							googleMap.setCenter(parentMarker.getPosition());
							googleMap.setZoom(mapSettings.zoom);
						}
					});
				});

				// Radial Controls
				const radialControls = mapControlsContainer.querySelectorAll('.google-map__controls [data-control-type="radial"] button');
				radialControls.forEach(button => {
					button.addEventListener("click", () => {
						const radialID = button.dataset.radialId; // This is the max distance (e.g., '500', '1000', 'all')
						if (log) console.log(`Google Maps (${mapFullID}): Radial filter clicked - ${radialID}`);

						let maxDistance = Infinity;
						if (radialID !== 'all') {
							maxDistance = Number(radialID);
							if (isNaN(maxDistance)) {
								console.warn(`Google Maps (${mapFullID}): Invalid radial distance value: ${radialID}`);
								return;
							}
						}

						// Determine start distance (from previous button or 0)
						let startDistance = 0;
						const prevButton = button.previousElementSibling;
						if (prevButton && prevButton.dataset.radialId && radialID !== 'all') {
							const prevDist = Number(prevButton.dataset.radialId);
							if (!isNaN(prevDist)) {
								startDistance = prevDist;
							}
						}

						// Reset UI
						radialControls.forEach(btn => btn.classList.remove('selected'));
						button.classList.add('selected');
						if (parentInfoWindow) parentInfoWindow.close();
						infoWindows.forEach(iw => iw.window.close());
						polylines.forEach(p => polylineOut(p.polyline));
						closeAllMarkerPanels(mapFullID);
						dimAllCircles(circles);

						// Filter markers by distance
						let visibleMarkers = [];
						const parentPosition = parentMarker.getPosition();
						childrenMarkers.forEach(child => {
							const distance = google.maps.geometry.spherical.computeDistanceBetween(parentPosition, child.getPosition());
							const isVisible = (radialID === 'all' || (distance <= maxDistance && distance >= startDistance));
							child.setVisible(isVisible);
							if (isVisible) visibleMarkers.push(child);
						});

						// Refit bounds
						if (visibleMarkers.length > 0) {
							fitBoundsToVisibleMarkers(googleMap, visibleMarkers.concat(parentMarker), mapSettings.zoom);
						} else {
							googleMap.setCenter(parentMarker.getPosition());
							googleMap.setZoom(mapSettings.zoom);
						}
					});
				});

			} else if (log) {
				console.log(`Google Maps (${mapFullID}): Controls container not found within .layout__google_map.`);
			}

			// --- Reusable Info Functions & Control Listeners ---

			const showMarkerInfo = (marker) => {
				if (!marker) return;

				// Close all other info windows (including parent)
				if (parentInfoWindow) parentInfoWindow.close();
				infoWindows.forEach((iw) => iw.window.close());

				// Animate out all polylines
				polylines.forEach((p) => animatePolylineOut(p.polyline));

				// Open this marker's info window (if it exists)
				const infoWindowEntry = infoWindows.find(iw => iw.markerID === marker.markerID);
				if (infoWindowEntry) {
					infoWindowEntry.window.open(googleMap, marker);
				}

				// Animate in the correct polyline for this marker
				const activePolyline = polylines.find(p => p.markerID === marker.markerID && p.type === marker.pathDisplay);
				if (activePolyline) {
					animatePolylineIn(activePolyline.polyline);
				}

				// Handle panels
				closeAllMarkerPanels(mapFullID);
				if (mapSettings.dialogType === 'drawer' || mapSettings.dialogType === 'both') {
					const panel = document.querySelector(`#${mapFullID} .google-map__infopanel[data-id="${marker.markerID}"]`);
					if (panel) panel.classList.add('selected');
				}

				// Handle radial circles
				dimAllCircles(circles); // Dim all first
				const latLng = marker.getPosition();
				let smallestCircle = null;
				circles.forEach(circle => {
					if (google.maps.geometry.spherical.computeDistanceBetween(latLng, circle.getCenter()) <= circle.getRadius()) {
						if (!smallestCircle || circle.getRadius() < smallestCircle.getRadius()) {
							smallestCircle = circle;
						}
					}
				});
				if (smallestCircle) {
					smallestCircle.setOptions({
						fillColor: rgbaToHex(mapSettings.radial?.radial_styles?.background_colour_active || 'rgba(255,255,0,0.2)').hex, // Default yellow semi-transparent
						fillOpacity: rgbaToHex(mapSettings.radial?.radial_styles?.background_colour_active || 'rgba(255,255,0,0.2)').opacity,
						zIndex: 100 // Bring to front
					});
				}
			};

			const hideMarkerInfo = (marker) => {
				if (!marker) return;

				const infoWindowEntry = infoWindows.find(iw => iw.markerID === marker.markerID);
				if (infoWindowEntry) infoWindowEntry.window.close();

				// Animate out associated polyline
				const activePolyline = polylines.find(p => p.markerID === marker.markerID && p.type === marker.pathDisplay);
				if (activePolyline) {
					animatePolylineOut(activePolyline.polyline);
				}
				// Close panel and dim circles
				closeAllMarkerPanels(mapFullID);
				dimAllCircles(circles);
			};

			// Add listeners to the actual map markers
			childrenMarkers.forEach(childMarker => {
				childMarker.addListener('mouseover', () => showMarkerInfo(childMarker));
				childMarker.addListener('mouseout', () => hideMarkerInfo(childMarker));
				childMarker.addListener('click', () => googleMap.panTo(childMarker.getPosition())); // Pan on click

				// Add close listener for the marker's info window
				const infoWindowEntry = infoWindows.find(iw => iw.markerID === childMarker.markerID);
				if (infoWindowEntry) {
					infoWindowEntry.window.addListener('closeclick', () => hideMarkerInfo(childMarker));
				}
			});

			// Add listeners to the marker control buttons
			if (mapControlsContainer) {
				const markerControls = mapControlsContainer.querySelectorAll('.google-map__controls [data-control-type="markers"] button');
				markerControls.forEach(button => {
					const markerID = button.dataset.markerId;
					const targetMarker = childrenMarkers.find(child => child.markerID === Number(markerID));

					if (targetMarker) {
						button.addEventListener("mouseover", () => {
							markerControls.forEach(btn => btn.classList.remove('selected'));
							button.classList.add('selected');
							showMarkerInfo(targetMarker);
						});

						button.addEventListener("click", () => {
							googleMap.panTo(targetMarker.getPosition());
							const lenis = getLenisInstance();
							if (lenis) {
								const header = document.getElementById('header');
								const offset = header ? -header.offsetHeight : 0;
								lenis.scrollTo(mapContainer, { offset: offset, lock: true });
							}
						});
					}
				});

				// Add a listener to the container to hide info when the mouse leaves the whole control area
				const markerControlsContainer = mapControlsContainer.querySelector('.google-map__controls [data-control-type="markers"]');
				if (markerControlsContainer) {
					markerControlsContainer.addEventListener('mouseleave', () => {
						const selectedButton = markerControlsContainer.querySelector('button.selected');
						if (selectedButton) {
							const markerID = selectedButton.dataset.markerId;
							const targetMarker = childrenMarkers.find(child => child.markerID === Number(markerID));
							if (targetMarker) hideMarkerInfo(targetMarker);
							selectedButton.classList.remove('selected');
						}
					});
				}
			}

			// --- Responsive Marker Resizing ---
			const allMarkers = [parentMarker, ...childrenMarkers];
			const responsiveMarkerConfig = globalSettings.responsiveMarkerConfig;

			// Only set up resizing if there are markers and a valid config is provided.
			if (allMarkers.length > 0 && responsiveMarkerConfig && responsiveMarkerConfig.breakpoints && responsiveMarkerConfig.scales) {
				const updateMarkerSizes = () => {
					const screenWidth = window.innerWidth;
					let sizeTier = 'large';
					if (screenWidth <= responsiveMarkerConfig.breakpoints.md) {
						sizeTier = 'medium';
					}
					if (screenWidth <= responsiveMarkerConfig.breakpoints.xs) {
						sizeTier = 'small';
					}

					const scale = responsiveMarkerConfig.scales[sizeTier];

					allMarkers.forEach(marker => {
						// --- Update Icon Size ---
						const icon = marker.getIcon();
						// Only resize icons that have an original size stored.
						if (icon && icon.url && marker.originalSize) {
							const newWidth = Math.round(marker.originalSize.width * scale);
							const newHeight = Math.round(marker.originalSize.height * scale);

							// Avoid re-setting the icon if the size hasn't changed, to prevent flicker.
							if (!icon.scaledSize || icon.scaledSize.width !== newWidth) {
								const newIcon = {
									...icon,
									scaledSize: new google.maps.Size(newWidth, newHeight),
									anchor: new google.maps.Point(newWidth / 2, newHeight), // Recalculate anchor to center bottom
									labelOrigin: (marker.getLabel()) ? new google.maps.Point(newWidth / 2, newHeight / 2.5) : icon.labelOrigin
								};
								marker.setIcon(newIcon);
							}
						}

						// --- Update Label Size ---
						const label = marker.getLabel();
						// Only resize labels that have an original font size stored.
						if (label && marker.originalLabelFontSize) {
							const newFontSize = Math.round(marker.originalLabelFontSize * scale);
							const newLabelFontSizePx = `${newFontSize}px`;

							// Avoid re-setting if size hasn't changed.
							if (label.fontSize !== newLabelFontSizePx) {
								const newLabel = {
									...label,
									fontSize: newLabelFontSizePx
								};
								marker.setLabel(newLabel);
							}
						}
					});
				};

				// Add a debounced resize listener to update sizes on viewport change
				window.addEventListener('resize', debounce(updateMarkerSizes, 250));
				updateMarkerSizes(); // Initial call to set the correct size on load
			} else if (log && allMarkers.length > 0) {
				console.warn(`Google Maps (${mapFullID}): Responsive marker config not found or invalid in globalSettings. Resizing disabled.`);
			}
		}); // End forEach map
	}).catch(e => {
		console.error("Google Maps: Failed to load API.", e);
	});

	if (log) console.log('googleMaps component initialization requested.');
} // End export function googleMaps


// --- Helper Functions (Keep these outside the main exported function but within the module scope) ---

function fitBoundsToVisibleMarkers(googleMap, googleMarkers, defaultZoom) {
    let bounds = new google.maps.LatLngBounds();
    let visibleCount = 0;

    googleMarkers.forEach(marker => {
        if (marker.getVisible()) {
            bounds.extend(marker.getPosition());
            visibleCount++;
        }
    });

    if (visibleCount > 0) {
        googleMap.fitBounds(bounds);

        // Adjust zoom if only one marker is visible or bounds are very small
        if (visibleCount === 1) {
            // If only one marker, fitBounds might zoom in too much. Set a default zoom.
             let listener = google.maps.event.addListenerOnce(googleMap, "idle", function() {
                googleMap.setZoom(defaultZoom || 14); // Use passed default or fallback
            });
        } else {
             // For multiple markers, listen for the map to be idle after fitting bounds,
             // then adjust the zoom to be tighter.
             let listener = google.maps.event.addListenerOnce(googleMap, "idle", function() {
                let currentZoom = googleMap.getZoom();
                // Increase the zoom by 1 to make it tighter, but don't exceed a max zoom level.
                googleMap.setZoom(Math.min(currentZoom + 1, 17)); // e.g., max zoom of 17
            });
        }
    }
    // If visibleCount is 0, the map remains unchanged (or you could center on parent)
}

function closeAllMarkerPanels(mapFullID) {
	// Use the full mapFullID passed which includes 'google-map--' prefix
	document.querySelectorAll(`#${mapFullID} .google-map__infopanel`).forEach(function(markerPanel) {
		markerPanel.classList.remove('selected');
	});
}

function dimAllCircles(circles) {
	circles.forEach(circle => {
		circle.setOptions({
			fillOpacity: 0, // Make fill transparent
			zIndex: 2 // Reset zIndex
		});
	});
}

function animatePolylineIn(polyline) {
	if (!polyline) return;
	let opacity = 0;
	// Clear previous timer if any (important for rapid clicks)
	if (polyline.animationTimerIn) clearInterval(polyline.animationTimerIn);
	if (polyline.animationTimerOut) clearInterval(polyline.animationTimerOut);

	polyline.animationTimerIn = setInterval(() => {
		opacity += 0.1;
		if (opacity >= 1) {
			clearInterval(polyline.animationTimerIn);
			polyline.setOptions({ strokeOpacity: 1 });
			delete polyline.animationTimerIn; // Clean up timer reference
		} else {
			polyline.setOptions({ strokeOpacity: opacity });
		}
	}, 30); // Faster animation
}

function animatePolylineOut(polyline) {
	if (!polyline || polyline.get('strokeOpacity') === 0) return; // Don't animate if already out
	let opacity = polyline.get('strokeOpacity') || 1; // Start from current opacity
	// Clear previous timer if any
	if (polyline.animationTimerIn) clearInterval(polyline.animationTimerIn);
	if (polyline.animationTimerOut) clearInterval(polyline.animationTimerOut);

	polyline.animationTimerOut = setInterval(() => {
		opacity -= 0.1;
		if (opacity <= 0) {
			clearInterval(polyline.animationTimerOut);
			polyline.setOptions({ strokeOpacity: 0 });
			delete polyline.animationTimerOut; // Clean up timer reference
		} else {
			polyline.setOptions({ strokeOpacity: opacity });
		}
	}, 30); // Faster animation
}

// Instantly hide polyline (used for filters)
function polylineOut(polyline) {
	if (!polyline) return;
	// Clear any running animations
	if (polyline.animationTimerIn) clearInterval(polyline.animationTimerIn);
	if (polyline.animationTimerOut) clearInterval(polyline.animationTimerOut);
	delete polyline.animationTimerIn;
	delete polyline.animationTimerOut;
	// Set opacity directly
	polyline.setOptions({ strokeOpacity: 0 });
}

function calculateLabelDestination(lat1, lon1, radialDistance) {
	// Simple approach: place label slightly North-East of the circle edge
	const EarthRadius = 6371e3; // meters
	const bearing = 45 * Math.PI / 180; // 45 degrees (North-East) in radians
	const lat1Rad = lat1 * Math.PI / 180;
	const lon1Rad = lon1 * Math.PI / 180;
	const angularDistance = radialDistance / EarthRadius;

	const lat2Rad = Math.asin(Math.sin(lat1Rad) * Math.cos(angularDistance) +
	                         Math.cos(lat1Rad) * Math.sin(angularDistance) * Math.cos(bearing));

	const lon2Rad = lon1Rad + Math.atan2(Math.sin(bearing) * Math.sin(angularDistance) * Math.cos(lat1Rad),
	                                   Math.cos(angularDistance) - Math.sin(lat1Rad) * Math.sin(lat2Rad));

	return {
		lat: lat2Rad * 180 / Math.PI,
		lng: lon2Rad * 180 / Math.PI
	};
}

function rgbaToHex(rgbaString = '') {
	const defaultValue = { hex: '#000000', opacity: 0 }; // Default fallback
	if (typeof rgbaString !== 'string') return defaultValue;

	const match = rgbaString.match(/^rgba?\((\d{1,3}),\s*(\d{1,3}),\s*(\d{1,3})(?:,\s*(\d?\.?\d+))?\)$/);

	if (!match) return defaultValue; // Return default if regex fails

	const r = parseInt(match[1], 10);
	const g = parseInt(match[2], 10);
	const b = parseInt(match[3], 10);
	const a = match[4] !== undefined ? parseFloat(match[4]) : 1; // Default alpha to 1 if not present

	// Clamp values
	const clamp = (num, min, max) => Math.min(Math.max(num, min), max);
	const clampedR = clamp(r, 0, 255);
	const clampedG = clamp(g, 0, 255);
	const clampedB = clamp(b, 0, 255);
	const clampedA = clamp(a, 0, 1);

	const rHex = clampedR.toString(16).padStart(2, '0');
	const gHex = clampedG.toString(16).padStart(2, '0');
	const bHex = clampedB.toString(16).padStart(2, '0');

	return {
		hex: `#${rHex}${gHex}${bHex}`,
		opacity: clampedA
	};
}

function htmlDecode(input) {
	if (typeof input !== 'string') return '';
    const doc = new DOMParser().parseFromString(input, "text/html");
    // Use textContent which automatically handles decoding
    return doc.documentElement.textContent || '';
}
